import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardMedia,
  Divider,
  Stack,
  Alert,
  TableSortLabel,
} from '@mui/material';
import {
  Add,
  Visibility,
  Edit,
  CheckCircle,
  Cancel,
  Print,
  FilterList,
  School,
  Download,
  CreditCard,
  LockReset,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { studentAPI, branchAPI, batchAPI, courseAPI, studentPaymentAPI } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import ConfirmDialog from '../../../components/ConfirmDialog';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const StudentList = () => {
  const navigate = useNavigate();
  const { user, isSuperAdmin } = useAuth();

  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [total, setTotal] = useState(0);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [detailsDialog, setDetailsDialog] = useState(false);
  const [rejectDialog, setRejectDialog] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [rejectionRemarks, setRejectionRemarks] = useState('');
  const [alumniDialog, setAlumniDialog] = useState(false);
  const [alumniRemarks, setAlumniRemarks] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [branches, setBranches] = useState([]);
  const [batches, setBatches] = useState([]);
  const [courses, setCourses] = useState([]);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    message: '',
    onConfirm: null,
  });
  const [resetPwDialog, setResetPwDialog] = useState(false);
  const [resetPwStudent, setResetPwStudent] = useState(null);
  const [resetPwValue, setResetPwValue] = useState('');
  const [resetPwLoading, setResetPwLoading] = useState(false);

  const [pdfLoading, setPdfLoading] = useState(false);

  const [paymentDialog, setPaymentDialog] = useState(false);
  const [paymentDialogStudent, setPaymentDialogStudent] = useState(null);
  const [paymentDialogData, setPaymentDialogData] = useState([]);
  const [paymentDialogLoading, setPaymentDialogLoading] = useState(false);
  // Payment data fetched alongside the details dialog
  const [detailsPayments, setDetailsPayments] = useState([]);
  const [detailsPaymentsLoading, setDetailsPaymentsLoading] = useState(false);

  // Filters
  const [filters, setFilters] = useState({
    status: '',
    centre: '',
    search: '',
    batch: '',
    course: '',
    startDate: '',
    endDate: '',
  });

  useEffect(() => {
    fetchStudents();
    if (isSuperAdmin) {
      fetchBranches();
    }
  }, [page, rowsPerPage, filters]);

  useEffect(() => {
    fetchBatches();
    fetchCourses();
  }, []);

  const fetchBranches = async () => {
    try {
      const response = await branchAPI.getAll({ limit: 1000 });
      setBranches(response.data.data || []);
    } catch (error) {
      console.error('Error fetching branches:', error);
    }
  };

  const fetchBatches = async () => {
    try {
      const response = await batchAPI.getAll();
      setBatches(response.data.data?.batches || []);
    } catch (error) {
      console.error('Error fetching batches:', error);
    }
  };

  const fetchCourses = async () => {
    try {
      const response = await courseAPI.getAll({ limit: 1000 });
      setCourses(response.data.data || []);
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  };

  const fetchStudents = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        ...filters,
      };

      // Remove empty filters
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null || params[key] === undefined) {
          delete params[key];
        }
      });

      const response = await studentAPI.getAll(params);
      setStudents(response.data.data || []);
      setTotal(response.data.pagination?.total || 0);
    } catch (error) {
      console.error('Error fetching students:', error);
      toast.error('Failed to load student applications');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, filters]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value,
    }));
    setPage(0); // Reset to first page when filters change
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewDetails = async (studentId) => {
    try {
      console.log('[DEBUG] handleViewDetails called for studentId:', studentId);
      const response = await studentAPI.getOne(studentId);
      setSelectedStudent(response.data.data.student);
      setDetailsDialog(true);
      // Fetch payment details alongside
      setDetailsPaymentsLoading(true);
      setDetailsPayments([]);
      try {
        console.log('[DEBUG] Fetching payment details for student in details dialog');
        const pRes = await studentPaymentAPI.getByStudent(studentId);
        console.log('[DEBUG] Details dialog payment response:', pRes);
        console.log('[DEBUG] Details dialog payment data:', pRes.data.data);
        setDetailsPayments(pRes.data.data || []);
      } catch (error) {
        console.error('[DEBUG] Error fetching payment in details dialog:', error);
        setDetailsPayments([]);
      } finally {
        setDetailsPaymentsLoading(false);
      }
    } catch (error) {
      console.error('Error fetching student details:', error);
      toast.error('Failed to load student details');
    }
  };

  const handleViewPayment = async (student) => {
    console.log('[DEBUG] handleViewPayment called for student:', student._id, student.personalInfo?.nameAsPerTC);
    setPaymentDialogStudent(student);
    setPaymentDialog(true);
    setPaymentDialogLoading(true);
    setPaymentDialogData([]);
    try {
      console.log('[DEBUG] Calling API: studentPaymentAPI.getByStudent');
      const res = await studentPaymentAPI.getByStudent(student._id);
      console.log('[DEBUG] API response:', res);
      console.log('[DEBUG] Payment data received:', res.data.data);
      setPaymentDialogData(res.data.data || []);
    } catch (error) {
      console.error('[DEBUG] Error fetching payment:', error);
      setPaymentDialogData([]);
    } finally {
      setPaymentDialogLoading(false);
    }
  };

  const handleApprove = (studentId) => {
    setConfirmDialog({
      open: true,
      title: 'Approve Application',
      message: 'Are you sure you want to approve this application?',
      onConfirm: async () => {
        try {
          setActionLoading(true);
          await studentAPI.approve(studentId, 'Application meets all requirements');
          toast.success('Application approved successfully');
          fetchStudents();
          setDetailsDialog(false);
        } catch (error) {
          console.error('Error approving application:', error);
          toast.error(error.response?.data?.message || 'Failed to approve application');
        } finally {
          setActionLoading(false);
        }
      },
    });
  };

  const handleRejectClick = (student) => {
    setSelectedStudent(student);
    setRejectionReason('');
    setRejectionRemarks('');
    setRejectDialog(true);
  };

  const handleRejectSubmit = async () => {
    if (!rejectionReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }

    try {
      setActionLoading(true);
      await studentAPI.reject(selectedStudent._id, rejectionReason, rejectionRemarks);
      toast.success('Application rejected');
      fetchStudents();
      setRejectDialog(false);
      setDetailsDialog(false);
    } catch (error) {
      console.error('Error rejecting application:', error);
      toast.error(error.response?.data?.message || 'Failed to reject application');
    } finally {
      setActionLoading(false);
    }
  };

  const handleConvertToAlumniClick = (student) => {
    setSelectedStudent(student);
    setAlumniRemarks('');
    setAlumniDialog(true);
  };

  const handleConvertToAlumniSubmit = () => {
    setConfirmDialog({
      open: true,
      title: 'Convert to Alumni',
      message: 'Are you sure you want to convert this student to alumni? This action cannot be undone.',
      onConfirm: async () => {
        try {
          setActionLoading(true);
          await studentAPI.convertToAlumni(selectedStudent._id, alumniRemarks);
          toast.success('Student converted to alumni successfully');
          fetchStudents();
          setAlumniDialog(false);
          setDetailsDialog(false);
        } catch (error) {
          console.error('Error converting to alumni:', error);
          toast.error(error.response?.data?.message || 'Failed to convert to alumni');
        } finally {
          setActionLoading(false);
        }
      },
    });
  };

  const handleResetPwClick = (student) => {
    setResetPwStudent(student);
    setResetPwValue('');
    setResetPwDialog(true);
  };

  const handleResetPwSubmit = async () => {
    if (!resetPwValue.trim() || resetPwValue.trim().length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setResetPwLoading(true);
    try {
      await studentAPI.resetPassword(resetPwStudent._id, resetPwValue.trim());
      toast.success('Password reset and credentials sent to student email');
      setResetPwDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to reset password');
    } finally {
      setResetPwLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (total === 0) return;

    setPdfLoading(true);
    try {
      const params = {
        ...filters,
        page: 1,
        limit: total,
      };

      // Remove empty filters
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null || params[key] === undefined) {
          delete params[key];
        }
      });

      const response = await studentAPI.getAll(params);
      const allStudents = response.data.data || [];

      const doc = new jsPDF('landscape', 'mm', 'a4');

      // Add header
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('BSS INFORMATION TECHNOLOGY MISSION', doc.internal.pageSize.getWidth() / 2, 15, { align: 'center' });

      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text('(Bharat Sevak Samaj, National Development Agency)', doc.internal.pageSize.getWidth() / 2, 22, { align: 'center' });
      doc.text('Established in 1952 by Planning Commission, Govt. of India', doc.internal.pageSize.getWidth() / 2, 27, { align: 'center' });

      // Add title
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('STUDENT LIST REPORT', doc.internal.pageSize.getWidth() / 2, 35, { align: 'center' });

      // Add metadata
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.text(`Generated on: ${new Date().toLocaleDateString('en-IN')}`, 14, 42);
      doc.text(`Total Records: ${allStudents.length}`, doc.internal.pageSize.getWidth() - 14, 42, { align: 'right' });

      // Add filters summary
      const filterParts = [];
      if (filters.status) filterParts.push(`Status: ${filters.status}`);
      if (filters.centre) {
        const branch = branches.find(b => b._id === filters.centre);
        if (branch) filterParts.push(`Centre: ${branch.name}`);
      }
      if (filters.search) filterParts.push(`Search: ${filters.search}`);
      if (filters.course) {
        const course = courses.find(c => c._id === filters.course);
        if (course) filterParts.push(`Course: ${course.name}`);
      }
      if (filters.startDate) filterParts.push(`From: ${new Date(filters.startDate).toLocaleDateString('en-IN')}`);
      if (filters.endDate) filterParts.push(`To: ${new Date(filters.endDate).toLocaleDateString('en-IN')}`);

      doc.text(`Filters: ${filterParts.length > 0 ? filterParts.join(' | ') : 'All Students'}`, 14, 47);

      // Prepare table data
      const tableData = allStudents.map((student, index) => [
        index + 1,
        student.formNumber || '-',
        student.personalInfo?.nameAsPerTC || '-',
        student.personalInfo?.phone || '-',
        student.personalInfo?.email || '-',
        student.centre?.name || '-',
        student.course?.name || '-',
        student.status?.toUpperCase() || '-',
        student.submittedAt ? new Date(student.submittedAt).toLocaleDateString('en-IN') : '-'
      ]);

      // Add table
      doc.autoTable({
        startY: 52,
        head: [['#', 'Form No', 'Name', 'Phone', 'Email', 'Centre', 'Course', 'Status', 'Submitted']],
        body: tableData,
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: [31, 118, 210], textColor: 255, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [245, 245, 245] },
        margin: { left: 14, right: 14 },
      });

      // Add footer
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setFont('helvetica', 'italic');
        doc.text(
          'This is a computer-generated document from BSS IT Mission ERP System',
          doc.internal.pageSize.getWidth() / 2,
          doc.internal.pageSize.getHeight() - 10,
          { align: 'center' }
        );
        doc.text(
          `Page ${i} of ${pageCount}`,
          doc.internal.pageSize.getWidth() - 14,
          doc.internal.pageSize.getHeight() - 10,
          { align: 'right' }
        );
      }

      // Save PDF
      const fileName = `Student_List_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);
      toast.success('PDF downloaded successfully');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate PDF');
    } finally {
      setPdfLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'success';
      case 'rejected':
        return 'error';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-IN');
  };

  const buildUploadUrl = (filePath) => {
    const base = process.env.REACT_APP_API_URL?.replace('/api', '') || '';
    const normalised = filePath.startsWith('uploads/') ? filePath : `uploads/students/${filePath}`;
    const encoded = normalised.split('/').map(encodeURIComponent).join('/');
    return `${base}/${encoded}`;
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          Student Applications
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={pdfLoading ? <CircularProgress size={16} /> : <Download />}
            onClick={handleDownloadPDF}
            disabled={total === 0 || pdfLoading}
          >
            {pdfLoading ? 'Preparing PDF...' : 'Download PDF'}
          </Button>
          {!isSuperAdmin && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => navigate('/admin/students/create')}
            >
              New Application
            </Button>
          )}
        </Box>
      </Box>

      {/* Filters */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <FilterList sx={{ mr: 1 }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Filters
          </Typography>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
                label="Status"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {isSuperAdmin && (
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Centre</InputLabel>
                <Select
                  name="centre"
                  value={filters.centre}
                  onChange={handleFilterChange}
                  label="Centre"
                  MenuProps={{ PaperProps: { style: { maxHeight: 320, overflowY: 'auto' } } }}
                >
                  <MenuItem value="">All Centres</MenuItem>
                  {branches.map((branch) => (
                    <MenuItem key={branch._id} value={branch._id}>
                      {branch.name} ({branch.code})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Search"
              name="search"
              value={filters.search}
              onChange={handleFilterChange}
              placeholder="Name or Form Number"
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Batch</InputLabel>
              <Select
                name="batch"
                value={filters.batch}
                onChange={handleFilterChange}
                label="Batch"
                MenuProps={{ PaperProps: { style: { maxHeight: 320, overflowY: 'auto' } } }}
              >
                <MenuItem value="">All Batches</MenuItem>
                {batches.map((b) => (
                  <MenuItem key={b._id} value={b._id}>
                    {b.name} ({b.year})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Course</InputLabel>
              <Select
                name="course"
                value={filters.course}
                onChange={handleFilterChange}
                label="Course"
                MenuProps={{ PaperProps: { style: { maxHeight: 320, overflowY: 'auto' } } }}
              >
                <MenuItem value="">All Courses</MenuItem>
                {courses.map((c) => (
                  <MenuItem key={c._id} value={c._id}>
                    {c.name} ({c.code})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="From Date"
              name="startDate"
              type="date"
              value={filters.startDate}
              onChange={handleFilterChange}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="To Date"
              name="endDate"
              type="date"
              value={filters.endDate}
              onChange={handleFilterChange}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Table */}
      <Paper elevation={2}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Form No</strong></TableCell>
                <TableCell><strong>Student Name</strong></TableCell>
                <TableCell><strong>Course</strong></TableCell>
                <TableCell><strong>Batch</strong></TableCell>
                <TableCell><strong>Centre</strong></TableCell>
                <TableCell><strong>Submitted Date</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                {isSuperAdmin && <TableCell align="center"><strong>Payment</strong></TableCell>}
                <TableCell align="center"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={isSuperAdmin ? 9 : 8} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : students.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={isSuperAdmin ? 9 : 8} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      No student applications found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                students.map((student) => (
                  <TableRow
                    key={student._id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleViewDetails(student._id)}
                  >
                    <TableCell>{student.formNumber || '-'}</TableCell>
                    <TableCell>{student.personalInfo?.nameAsPerTC || '-'}</TableCell>
                    <TableCell>
                      {student.course?.name || '-'}
                      <br />
                      <Typography variant="caption" color="text.secondary">
                        {student.course?.code || ''}
                      </Typography>
                    </TableCell>
                    <TableCell>{student.batch?.name || '-'}</TableCell>
                    <TableCell>
                      {student.centre?.name || '-'}
                      <br />
                      <Typography variant="caption" color="text.secondary">
                        {student.centre?.code || ''}
                      </Typography>
                    </TableCell>
                    <TableCell>{formatDate(student.submittedAt)}</TableCell>
                    <TableCell>
                      <Chip
                        label={student.status?.toUpperCase() || 'UNKNOWN'}
                        color={getStatusColor(student.status)}
                        size="small"
                      />
                    </TableCell>
                    {isSuperAdmin && (
                      <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                        <Tooltip title="View Payment Details">
                          <IconButton
                            size="small"
                            color="secondary"
                            onClick={() => handleViewPayment(student)}
                          >
                            <CreditCard fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    )}
                    <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleViewDetails(student._id)}
                        >
                          <Visibility />
                        </IconButton>
                      </Tooltip>

                      {student.status === 'rejected' && (user.role === 'branchadmin' && student.centre?._id === user.branch) && (
                        <Tooltip title="Edit & Resubmit">
                          <IconButton
                            size="small"
                            color="info"
                            onClick={() => navigate(`/admin/students/edit/${student._id}`)}
                          >
                            <Edit />
                          </IconButton>
                        </Tooltip>
                      )}

                      {isSuperAdmin && (
                        <Tooltip title="Edit Student">
                          <IconButton
                            size="small"
                            color="info"
                            onClick={() => navigate(`/admin/students/edit/${student._id}?superadmin=true`)}
                          >
                            <Edit />
                          </IconButton>
                        </Tooltip>
                      )}

                      {isSuperAdmin && student.status === 'pending' && (
                        <>
                          <Tooltip title="Approve">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleApprove(student._id)}
                            >
                              <CheckCircle />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Reject">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleRejectClick(student)}
                            >
                              <Cancel />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}

                      {user.role === 'branchadmin' && student.status === 'approved' && (
                        <Tooltip title="Convert to Alumni">
                          <IconButton
                            size="small"
                            color="info"
                            onClick={() => handleConvertToAlumniClick(student)}
                          >
                            <School />
                          </IconButton>
                        </Tooltip>
                      )}

                      {isSuperAdmin && student.status === 'approved' && (
                        <Tooltip title="Reset Password">
                          <IconButton
                            size="small"
                            color="warning"
                            onClick={() => handleResetPwClick(student)}
                          >
                            <LockReset />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[25, 50, 100]}
        />
      </Paper>

      {/* Details Dialog */}
      <Dialog
        open={detailsDialog}
        onClose={() => setDetailsDialog(false)}
        maxWidth="md"
        fullWidth
      >
        {selectedStudent && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Student Application Details
                </Typography>
                <Chip
                  label={selectedStudent.status?.toUpperCase()}
                  color={getStatusColor(selectedStudent.status)}
                  size="small"
                />
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={3}>
                {/* Photo */}
                {selectedStudent.photo && (
                  <Grid item xs={12} md={3}>
                    <Card>
                      <CardMedia
                        component="img"
                        height="200"
                        image={buildUploadUrl(selectedStudent.photo)}
                        alt="Student Photo"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          console.error('Failed to load image:', selectedStudent.photo);
                        }}
                      />
                    </Card>
                  </Grid>
                )}

                {/* Basic Info */}
                <Grid item xs={12} md={selectedStudent.photo ? 9 : 12}>
                  <Typography variant="subtitle2" color="text.secondary">Form Number</Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>{selectedStudent.formNumber}</Typography>

                  <Typography variant="subtitle2" color="text.secondary">BSS Form ID</Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {selectedStudent.bssFormId || (
                      <Typography component="span" variant="body2" color="text.secondary" fontStyle="italic">
                        Not available (registered before this field was added)
                      </Typography>
                    )}
                  </Typography>

                  <Typography variant="subtitle2" color="text.secondary">Course</Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {selectedStudent.course?.name} ({selectedStudent.course?.code})
                  </Typography>

                  <Typography variant="subtitle2" color="text.secondary">Centre</Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {selectedStudent.centre?.name} ({selectedStudent.centre?.code})
                  </Typography>
                </Grid>

                {/* Personal Information */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Personal Information</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">Name (as per TC)</Typography>
                  <Typography variant="body1">{selectedStudent.personalInfo?.nameAsPerTC}</Typography>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Typography variant="subtitle2" color="text.secondary">Date of Birth</Typography>
                  <Typography variant="body1">{formatDate(selectedStudent.personalInfo?.dateOfBirth)}</Typography>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Typography variant="subtitle2" color="text.secondary">Gender</Typography>
                  <Typography variant="body1">{selectedStudent.personalInfo?.gender}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">Phone</Typography>
                  <Typography variant="body1">{selectedStudent.personalInfo?.phone}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">Email</Typography>
                  <Typography variant="body1">{selectedStudent.personalInfo?.email || '-'}</Typography>
                </Grid>

                {/* Addresses */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Addresses</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Permanent Address</Typography>
                  <Typography variant="body2">
                    {selectedStudent.permanentAddress?.street}<br />
                    {selectedStudent.permanentAddress?.city}, {selectedStudent.permanentAddress?.district}<br />
                    {selectedStudent.permanentAddress?.state} - {selectedStudent.permanentAddress?.pincode}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Communication Address</Typography>
                  <Typography variant="body2">
                    {selectedStudent.communicationAddress?.street}<br />
                    {selectedStudent.communicationAddress?.city}, {selectedStudent.communicationAddress?.district}<br />
                    {selectedStudent.communicationAddress?.state} - {selectedStudent.communicationAddress?.pincode}
                  </Typography>
                </Grid>

                {/* Father/Guardian */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Father/Guardian Information</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">Name</Typography>
                  <Typography variant="body1">{selectedStudent.fatherOrGuardian?.name}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">Occupation</Typography>
                  <Typography variant="body1">{selectedStudent.fatherOrGuardian?.occupation || '-'}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">Phone</Typography>
                  <Typography variant="body1">{selectedStudent.fatherOrGuardian?.phone || '-'}</Typography>
                </Grid>

                {/* Educational Qualifications */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Educational Qualifications</Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Examination</TableCell>
                          <TableCell>University/Board</TableCell>
                          <TableCell>Year</TableCell>
                          <TableCell>Class/Division</TableCell>
                          <TableCell>Grade/%</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedStudent.educationalQualifications?.map((qual, index) => (
                          <TableRow key={index}>
                            <TableCell>{qual.examination}</TableCell>
                            <TableCell>{qual.universityOrBoard}</TableCell>
                            <TableCell>{qual.yearOfPassing}</TableCell>
                            <TableCell>{qual.classOrDivision}</TableCell>
                            <TableCell>{qual.gradeOrPercentage}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>

                {/* Employment */}
                {selectedStudent.employment?.isEmployed && (
                  <>
                    <Grid item xs={12}>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Employment Details</Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="text.secondary">Employer</Typography>
                      <Typography variant="body1">{selectedStudent.employment?.employerName}</Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="text.secondary">Designation</Typography>
                      <Typography variant="body1">{selectedStudent.employment?.designation}</Typography>
                    </Grid>
                  </>
                )}

                {/* Certificate */}
                {isSuperAdmin && selectedStudent.certificate && (
                  <>
                    <Grid item xs={12}>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Certificate</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      {selectedStudent.certificate.endsWith('.pdf') ? (
                        <Button
                          variant="outlined"
                          onClick={() => {
                            const certPath = selectedStudent.certificate.startsWith('uploads/')
                              ? selectedStudent.certificate
                              : `uploads/certificates/${selectedStudent.certificate}`;
                            window.open(buildUploadUrl(certPath), '_blank');
                          }}
                        >
                          View Certificate (PDF)
                        </Button>
                      ) : (
                        <Card sx={{ maxWidth: 300 }}>
                          <CardMedia
                            component="img"
                            height="200"
                            image={buildUploadUrl(
                              selectedStudent.certificate.startsWith('uploads/')
                                ? selectedStudent.certificate
                                : `uploads/certificates/${selectedStudent.certificate}`
                            )}
                            alt="Student Certificate"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              console.error('Failed to load certificate:', selectedStudent.certificate);
                            }}
                          />
                        </Card>
                      )}
                    </Grid>
                  </>
                )}

                {/* Payment Details */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Payment Details</Typography>
                  {detailsPaymentsLoading ? (
                    <CircularProgress size={20} />
                  ) : detailsPayments.length === 0 ? (
                    <Alert severity="warning" icon={<CreditCard />}>
                      No payment details recorded for this student.
                    </Alert>
                  ) : (
                    <Stack spacing={1.5}>
                      {detailsPayments.map((p, i) => (
                        <Box key={p._id || i} sx={{ p: 2, bgcolor: p.isBatchPayment ? '#f0f7ff' : '#f8faff', border: '1px solid #e2eaf5', borderRadius: 2 }}>
                          <Grid container spacing={1.5}>
                            <Grid item xs={6} sm={3}>
                              <Typography variant="caption" color="text.secondary">Type</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600, textTransform: 'capitalize' }}>
                                {p.paymentType?.replace('_', ' ')}
                                {p.isBatchPayment && ' (Batch)'}
                              </Typography>
                            </Grid>
                            <Grid item xs={6} sm={3}>
                              <Typography variant="caption" color="text.secondary">Amount</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>₹{p.amount?.toLocaleString('en-IN')}</Typography>
                            </Grid>
                            <Grid item xs={6} sm={3}>
                              <Typography variant="caption" color="text.secondary">Date</Typography>
                              <Typography variant="body2">{formatDate(p.transferDate)}</Typography>
                            </Grid>
                            <Grid item xs={6} sm={3}>
                              <Typography variant="caption" color="text.secondary">Recorded On</Typography>
                              <Typography variant="body2">{formatDate(p.createdAt)}</Typography>
                            </Grid>
                            {p.isBatchPayment && p.studentCount && (
                              <Grid item xs={6} sm={4}>
                                <Typography variant="caption" color="text.secondary">Student Count</Typography>
                                <Typography variant="body2">{p.studentCount} students</Typography>
                              </Grid>
                            )}
                            {p.status && (
                              <Grid item xs={6} sm={4}>
                                <Typography variant="caption" color="text.secondary">Status</Typography>
                                <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>{p.status}</Typography>
                              </Grid>
                            )}
                            {p.referenceNumber && (
                              <Grid item xs={6} sm={4}>
                                <Typography variant="caption" color="text.secondary">Reference / Transaction No.</Typography>
                                <Typography variant="body2">{p.referenceNumber}</Typography>
                              </Grid>
                            )}
                            {p.fromBank && (
                              <Grid item xs={6} sm={4}>
                                <Typography variant="caption" color="text.secondary">From Bank</Typography>
                                <Typography variant="body2">{p.fromBank}</Typography>
                              </Grid>
                            )}
                          </Grid>
                        </Box>
                      ))}
                    </Stack>
                  )}
                </Grid>

                {/* Rejection Reason */}
                {selectedStudent.status === 'rejected' && selectedStudent.rejectionReason && (
                  <>
                    <Grid item xs={12}>
                      <Divider sx={{ my: 2 }} />
                      <Alert severity="error">
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Rejection Reason:</Typography>
                        <Typography variant="body2">{selectedStudent.rejectionReason}</Typography>
                      </Alert>
                    </Grid>
                  </>
                )}
              </Grid>
            </DialogContent>
            <DialogActions>
              {isSuperAdmin && selectedStudent.status === 'pending' && (
                <>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<CheckCircle />}
                    onClick={() => handleApprove(selectedStudent._id)}
                    disabled={actionLoading}
                  >
                    Approve
                  </Button>
                  <Button
                    variant="contained"
                    color="error"
                    startIcon={<Cancel />}
                    onClick={() => handleRejectClick(selectedStudent)}
                    disabled={actionLoading}
                  >
                    Reject
                  </Button>
                </>
              )}
              <Button onClick={() => setDetailsDialog(false)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialog} onClose={() => setRejectDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Application</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 3 }}>
            Please provide a clear reason for rejection. The centre will be able to edit and resubmit the application.
          </Alert>
          <TextField
            label="Rejection Reason"
            fullWidth
            required
            multiline
            rows={3}
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Additional Remarks (Optional)"
            fullWidth
            multiline
            rows={2}
            value={rejectionRemarks}
            onChange={(e) => setRejectionRemarks(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialog(false)} disabled={actionLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleRejectSubmit}
            disabled={actionLoading || !rejectionReason.trim()}
          >
            {actionLoading ? 'Rejecting...' : 'Confirm Rejection'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Convert to Alumni Dialog */}
      <Dialog open={alumniDialog} onClose={() => setAlumniDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Convert to Alumni</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 3 }}>
            This will convert the student to alumni status. This action cannot be undone.
          </Alert>
          <TextField
            label="Remarks (Optional)"
            fullWidth
            multiline
            rows={3}
            value={alumniRemarks}
            onChange={(e) => setAlumniRemarks(e.target.value)}
            placeholder="Add any remarks about this conversion..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAlumniDialog(false)} disabled={actionLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="info"
            onClick={handleConvertToAlumniSubmit}
            disabled={actionLoading}
            startIcon={<School />}
          >
            {actionLoading ? 'Converting...' : 'Convert to Alumni'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Payment Details Dialog */}
      <Dialog open={paymentDialog} onClose={() => setPaymentDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CreditCard color="secondary" />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>Payment Details</Typography>
          </Box>
          {paymentDialogStudent && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {paymentDialogStudent.personalInfo?.nameAsPerTC} &nbsp;·&nbsp; {paymentDialogStudent.formNumber}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers>
          {paymentDialogLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : paymentDialogData.length === 0 ? (
            <Alert severity="warning" icon={<CreditCard />} sx={{ mt: 1 }}>
              No payment details recorded for this student.
            </Alert>
          ) : (
            <Stack spacing={2} sx={{ mt: 1 }}>
              {paymentDialogData.map((p, i) => (
                <Paper key={p._id || i} variant="outlined" sx={{ p: 2.5, borderRadius: 2, bgcolor: p.isBatchPayment ? '#f0f7ff' : 'inherit' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <Chip
                        label={p.paymentType?.replace('_', ' ').toUpperCase()}
                        color="primary"
                        size="small"
                        sx={{ textTransform: 'capitalize' }}
                      />
                      {p.isBatchPayment && (
                        <Chip
                          label={`Batch (${p.studentCount} students)`}
                          color="info"
                          size="small"
                          variant="outlined"
                        />
                      )}
                      {p.status && (
                        <Chip
                          label={p.status.toUpperCase()}
                          color={p.status === 'verified' ? 'success' : p.status === 'rejected' ? 'error' : 'warning'}
                          size="small"
                        />
                      )}
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'success.main' }}>
                      ₹{p.amount?.toLocaleString('en-IN')}
                    </Typography>
                  </Box>
                  <Grid container spacing={1.5}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Payment Date</Typography>
                      <Typography variant="body2">{formatDate(p.transferDate)}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Recorded On</Typography>
                      <Typography variant="body2">{formatDate(p.createdAt)}</Typography>
                    </Grid>
                    {p.referenceNumber && (
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary">
                          {p.paymentType === 'upi' ? 'UPI Transaction ID' :
                           p.paymentType === 'card' ? 'Card Reference No.' :
                           p.isBatchPayment ? 'Payment Reference' :
                           'Slip / Reference No.'}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{p.referenceNumber}</Typography>
                      </Grid>
                    )}
                    {p.fromBank && (
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary">From Bank</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{p.fromBank}</Typography>
                      </Grid>
                    )}
                    {p.createdBy?.name && (
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary">Recorded by</Typography>
                        <Typography variant="body2">{p.createdBy.name}</Typography>
                      </Grid>
                    )}
                  </Grid>
                </Paper>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmColor="primary"
      />

      {/* Reset Student Password Dialog */}
      <Dialog open={resetPwDialog} onClose={() => setResetPwDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Reset Student Password</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Set a new password for <strong>{resetPwStudent?.personalInfo?.nameAsPerTC}</strong>.
            Login credentials will be sent to <strong>{resetPwStudent?.personalInfo?.email}</strong>.
          </Typography>
          <TextField
            fullWidth
            label="New Password"
            type="text"
            value={resetPwValue}
            onChange={(e) => setResetPwValue(e.target.value)}
            disabled={resetPwLoading}
            helperText="Minimum 6 characters"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetPwDialog(false)} disabled={resetPwLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleResetPwSubmit}
            disabled={resetPwLoading || !resetPwValue.trim()}
          >
            {resetPwLoading ? <CircularProgress size={20} /> : 'Reset & Send Email'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StudentList;
